import { useMemo, useCallback } from 'react';
import { CohortDetail } from 'App.types';
import { isCohortEditable, isCohortInProgress, isCohortCompleted } from '../utils';

export interface SiteInfo {
  captain_id: string;
  learn_url?: string;
  site_status?: string;
  notifications?: {
    status?: string;
    migration_date?: string;
    start_suggested_date?: string;
    end_suggested_date?: string;
    resend_number?: number;
    is_accepted?: boolean;
  };
}

interface NotificationData {
  daysUntilDowntime: number;
  canSendNotifications: boolean;
  canSendReminders: boolean;
}

interface UseCohortNotificationsOptions {
  cohort: CohortDetail | null;
  sites: SiteInfo[];
  daysForNotifications?: number; // TODO: Set to 14 after testing
  daysForReminders?: number; // TODO: Set to 8 after testing
}

export const isEligibleForReminder = (site: SiteInfo): boolean => {
  if (site.site_status === 'ACCEPTED' || site.site_status === 'DECLINED') {
    return false;
  }

  if (site.notifications?.resend_number && site.notifications.resend_number > 0) {
    return false;
  }

  const isOriginalSent = site.site_status === 'SENT' || site.notifications?.status === 'SENT';

  if (!isOriginalSent) {
    return false;
  }

  return true;
};

export const useCohortNotifications = ({
  cohort,
  sites,
  daysForNotifications = 0,
  daysForReminders = 0,
}: UseCohortNotificationsOptions) => {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;

  const calculateDaysUntilDowntime = useCallback(
    (downtimeDate: string): number => {
      const now = new Date();
      const downtime = new Date(downtimeDate);
      const msUntilDowntime = downtime.getTime() - now.getTime();
      return Math.ceil(msUntilDowntime / MS_PER_DAY);
    },
    [MS_PER_DAY],
  );

  const notificationData = useMemo((): NotificationData => {
    if (!cohort?.downtime_date) {
      return {
        daysUntilDowntime: 0,
        canSendNotifications: false,
        canSendReminders: false,
      };
    }

    const daysUntilDowntime = calculateDaysUntilDowntime(cohort.downtime_date);

    const canSendNotifications =
      isCohortEditable(cohort.migration_status) && daysUntilDowntime >= daysForNotifications;

    const hasEligibleSites = sites.some((site) => isEligibleForReminder(site));

    const canSendReminders =
      !isCohortEditable(cohort.migration_status) &&
      daysUntilDowntime > daysForReminders &&
      hasEligibleSites;

    return {
      daysUntilDowntime,
      canSendNotifications,
      canSendReminders,
    };
  }, [
    cohort?.downtime_date,
    cohort?.migration_status,
    sites,
    daysForNotifications,
    daysForReminders,
    calculateDaysUntilDowntime,
  ]);

  const reminderEligibleSites = useMemo(
    () => sites.filter((site) => isEligibleForReminder(site)),
    [sites],
  );

  const sitesWithReminders = useMemo(
    () =>
      sites.filter(
        (site) => site.notifications?.resend_number && site.notifications.resend_number > 0,
      ),
    [sites],
  );

  const isNotificationWarning = useMemo(
    () =>
      !notificationData.canSendNotifications &&
      notificationData.daysUntilDowntime < daysForNotifications &&
      isCohortEditable(cohort?.migration_status),
    [
      notificationData.canSendNotifications,
      notificationData.daysUntilDowntime,
      daysForNotifications,
      cohort?.migration_status,
    ],
  );

  const isReminderWarning = useMemo(
    () =>
      !notificationData.canSendReminders &&
      notificationData.daysUntilDowntime <= daysForReminders &&
      notificationData.daysUntilDowntime > 0 &&
      !isCohortEditable(cohort?.migration_status),
    [
      notificationData.canSendReminders,
      notificationData.daysUntilDowntime,
      daysForReminders,
      cohort?.migration_status,
    ],
  );

  const getNotificationStatusText = () => {
    if (!cohort) return '';

    const { daysUntilDowntime, canSendNotifications } = notificationData;

    if (isCohortEditable(cohort.migration_status)) {
      if (canSendNotifications) {
        return `Notifications can be sent (${daysUntilDowntime} days until downtime)`;
      }
      if (daysUntilDowntime < daysForNotifications) {
        return `Too close to downtime to send notifications (${daysUntilDowntime} days remaining)`;
      }

      return 'Notifications not available yet';
    }

    return 'Notifications already sent';
  };

  const getReminderStatusText = () => {
    if (!cohort) return '';

    const { daysUntilDowntime, canSendReminders } = notificationData;

    if (isCohortEditable(cohort.migration_status)) {
      return 'Reminders available after notifications are sent';
    }

    if (isCohortInProgress(cohort.migration_status) || isCohortCompleted(cohort.migration_status)) {
      return 'Reminders no longer available (cohort in progress/completed)';
    }

    if (canSendReminders) {
      return `Reminders can be sent to ${reminderEligibleSites.length} sites (${daysUntilDowntime} days until downtime)`;
    }

    if (daysUntilDowntime <= daysForReminders) {
      return `Too close to downtime to send reminders (${daysUntilDowntime} days remaining)`;
    }

    if (sites.length > 0 && reminderEligibleSites.length === 0) {
      return 'All sites have responded or received a reminder';
    }

    return 'Reminders not available';
  };

  const getFormattedDowntimeDate = () => {
    if (!cohort?.downtime_date) return '';

    try {
      return new Date(cohort.downtime_date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      });
    } catch {
      return cohort.downtime_date;
    }
  };

  const getTimeUntilDowntime = () => {
    const { daysUntilDowntime } = notificationData;

    if (daysUntilDowntime < 0) {
      return `${Math.abs(daysUntilDowntime)} days overdue`;
    }
    if (daysUntilDowntime === 0) {
      return 'Today';
    }
    if (daysUntilDowntime === 1) {
      return 'Tomorrow';
    }

    return `${daysUntilDowntime} days`;
  };

  const canPerformAction = (action: 'notifications' | 'reminders' | 'edit') => {
    if (!cohort) return false;

    switch (action) {
      case 'notifications':
        return notificationData.canSendNotifications;
      case 'reminders':
        return notificationData.canSendReminders;
      case 'edit':
        return isCohortEditable(cohort.migration_status);
      default:
        return false;
    }
  };

  const getActionWarning = (action: 'notifications' | 'reminders') => {
    if (!cohort) return '';

    const { daysUntilDowntime } = notificationData;

    switch (action) {
      case 'notifications':
        if (isNotificationWarning) {
          return `Notifications cannot be sent because there are only ${daysUntilDowntime} days until downtime (minimum ${daysForNotifications} days required)`;
        }
        break;
      case 'reminders':
        if (isReminderWarning) {
          return `Reminders cannot be sent because there are only ${daysUntilDowntime} days until downtime (minimum ${daysForReminders + 1} days required)`;
        }
        break;
    }

    return '';
  };

  return {
    notificationData,
    reminderEligibleSites,
    sitesWithReminders,
    isNotificationWarning,
    isReminderWarning,
    canPerformAction,
    getNotificationStatusText,
    getReminderStatusText,
    getFormattedDowntimeDate,
    getTimeUntilDowntime,
    getActionWarning,
    totalSites: sites.length,
    eligibleForReminders: reminderEligibleSites.length,
    alreadyReminded: sitesWithReminders.length,
    daysUntilDowntime: notificationData.daysUntilDowntime,
    isOverdue: notificationData.daysUntilDowntime < 0,
    isToday: notificationData.daysUntilDowntime === 0,
    isTomorrow: notificationData.daysUntilDowntime === 1,
  };
};
